/**
 * Measured SFOC behind the fitted curves.
 *
 * Every ship's telemetry carries SFOC as measured per engine and fuel
 * (`dg{n}_{fuel}_sfoc`, pilot fuel as `dg{n}_lng_ign_sfoc`) and the engine's
 * load (`dg{n}_load`). For each selected curve the viewer fetches those two
 * columns over the Model Viewer's date range - once, as a scatter through the
 * ordinary `/api/plot` - and draws them three ways from the same points:
 *
 *   - **Scatter** - every point, in the curve's colour, behind the curve.
 *   - **Heatmap** - the points binned on the chart's own grid, for when a
 *     scatter of tens of thousands of points is one solid blob.
 *   - **Load** - where on the load axis the engine actually ran, as a faint
 *     histogram along the bottom (what "Load usage" used to fetch separately,
 *     from a column 11 of 56 ships do not have).
 *
 * One fetch, three views, so switching between them never re-queries - and the
 * points carry their timestamps (`slice_values`) for the time slicer to come.
 *
 * Load is plotted in percent, as the curve formulas take it. Whether the
 * telemetry stores it as 80 or 0.8 is not documented, so each fetch is looked
 * at: loads whose 95th percentile is at most 1.5 are fractions and are scaled.
 */

import { Atom } from '../../state/atom.ts';
import { getColumns, runPlot } from '../../api/endpoints.ts';
import { timeColumn } from '../../state/plot-state.ts';
import {
  modelEnd,
  modelStart,
  sfocMeasured,
  sfocTables,
  type SfocMeasuredMode as MeasuredMode,
} from '../../state/store.ts';
import type { HeatmapResponse } from '../../api/types.ts';
import { userMessage } from '../../api/errors.ts';

// The choice itself lives in the store, where the URL reads it.
export { sfocMeasured, sfocTables };
export type { MeasuredMode };

export const MEASURED_MODES: Array<{ id: MeasuredMode; label: string; title: string }> = [
  { id: 'off', label: 'Off', title: 'Curves only' },
  { id: 'scatter', label: 'Scatter', title: 'Every measured point behind its curve' },
  { id: 'heatmap', label: 'Heatmap', title: 'Measured points binned by load and SFOC' },
  { id: 'load', label: 'Load', title: 'Where on the load axis each engine ran' },
];

/**
 * The time slicer's window, epoch seconds; open ends mean "to the edge of the
 * date range". Untouched, everything in the range shows and the curves are the
 * versions in force at its end.
 */
export interface TimeSlice {
  min: number | null;
  max: number | null;
}

export const sfocSlice = new Atom<TimeSlice>({ min: null, max: null });

// A window belongs to the range it was drawn in: a new range starts whole.
const wholeRange = (): void => {
  const s = sfocSlice.value;
  if (s.min != null || s.max != null) sfocSlice.set({ min: null, max: null });
};
modelStart.subscribe(wholeRange);
modelEnd.subscribe(wholeRange);

/** Points fetched per curve. Plenty for a cloud and for binning; a scatter of
 *  Explore's full 100 000 per curve would make several curves slow to draw. */
const MEASURED_POINTS = 20_000;

/** Standard SFOC y-axis bands per fuel (g/kWh). */
export const SFOC_BANDS: Record<string, [number, number]> = {
  HFO: [180, 280],
  VLSFO: [180, 280],
  MGO: [180, 280],
  B100: [180, 280],
  LNG: [150, 250],
  IGNITION: [1, 20],
};

/**
 * The plausible range a measured value must fall in to be fetched at all:
 * half the fuel's band to twice it. Start-up and shutdown rows produce SFOC in
 * the thousands (little power, some fuel), and one of those stretches every
 * axis and every heatmap bin while saying nothing about the engine.
 */
function plausible(fuel: string): [number, number] {
  const band = SFOC_BANDS[fuel.toUpperCase()];
  return band ? [band[0] * 0.5, band[1] * 2] : [0, 2000];
}

/** The measured-SFOC column for an engine and fuel, or null for a generic curve. */
export function measuredColumn(engine: string, fuel: string): string | null {
  const n = /(\d+)/.exec(engine)?.[1];
  if (!n) return null;
  const f = fuel.toUpperCase() === 'IGNITION' ? 'lng_ign' : fuel.toLowerCase();
  return `dg${n}_${f}_sfoc`;
}

/** The engine's load column, or null for a generic curve. */
export function loadColumn(engine: string): string | null {
  const n = /(\d+)/.exec(engine)?.[1];
  return n ? `dg${n}_load` : null;
}

export interface MeasuredPoints {
  /** Engine load, percent. */
  load: number[];
  /** Measured SFOC, g/kWh. */
  sfoc: number[];
  /** Epoch seconds per point, for the time slicer. Null if not returned. */
  time: number[] | null;
  /** Rows the query matched before downsampling. */
  sourceRows: number;
  /** True when the telemetry stored load as a fraction and it was scaled. */
  scaled: boolean;
}

/** A curve with nothing to draw, and why - in a few words, never a server dump. */
export interface MeasuredGap {
  error: string;
}

export type MeasuredEntry = MeasuredPoints | 'loading' | MeasuredGap;

/** Fetched points per curve and window. Replaced (not mutated) on each change. */
export const measuredCache = new Atom<ReadonlyMap<string, MeasuredEntry>>(new Map());

export function measuredKey(ship: string, engine: string, fuel: string, start: string, end: string): string {
  return `${ship}‖${engine}‖${fuel}‖${start}‖${end}`;
}

function put(key: string, entry: MeasuredEntry): void {
  const next = new Map(measuredCache.value);
  next.set(key, entry);
  measuredCache.set(next);
}

/**
 * Each ship's column names, fetched once. Ships do not all carry every fuel's
 * column - most have no `vlsfo` one - and asking for a column a ship lacks gets
 * back an error listing all ~500 it does have. Checking first turns that into
 * "no dg1_vlsfo_sfoc column". Null when the list itself could not be had, in
 * which case the fetch goes ahead and the server decides.
 */
const shipColumns = new Map<string, Promise<ReadonlySet<string> | null>>();

function columnsOf(ship: string): Promise<ReadonlySet<string> | null> {
  let pending = shipColumns.get(ship);
  if (!pending) {
    pending = getColumns(ship)
      .then((cols) => new Set(cols.map((c) => c.name)))
      .catch(() => null);
    shipColumns.set(ship, pending);
  }
  return pending;
}

/** The first line of an error, without any column list, short enough for a note. */
export function shortError(message: string): string {
  const first = message.split('\n')[0]!.split(/Available columns/i)[0]!.trim().replace(/[.;,:]$/, '');
  return first.length > 100 ? `${first.slice(0, 99)}…` : first;
}

function percentile95(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(0.95 * (sorted.length - 1))]!;
}

/** Fetch one curve's measured points for a window, unless cached or in flight. */
export function ensureMeasured(ship: string, engine: string, fuel: string, start: string, end: string): void {
  const key = measuredKey(ship, engine, fuel, start, end);
  if (measuredCache.value.has(key)) return;
  const sfocCol = measuredColumn(engine, fuel);
  const loadCol = loadColumn(engine);
  if (!sfocCol || !loadCol) return;
  const [lo, hi] = plausible(fuel);
  const time = timeColumn();
  put(key, 'loading');
  void fetchMeasured(key, ship, sfocCol, loadCol, lo, hi, time, start, end);
}

async function fetchMeasured(
  key: string,
  ship: string,
  sfocCol: string,
  loadCol: string,
  lo: number,
  hi: number,
  time: string,
  start: string,
  end: string,
): Promise<void> {
  const cols = await columnsOf(ship);
  const absent = cols ? [sfocCol, loadCol].find((c) => !cols.has(c)) : undefined;
  if (absent) {
    put(key, { error: `no ${absent} column` });
    return;
  }
  await runPlot({
    plot_type: 'scatter',
    ship_id: ship,
    start_date: start,
    end_date: end,
    x_column: loadCol,
    y_left: [sfocCol],
    y_right: [],
    max_points: MEASURED_POINTS,
    filters: {
      logic: 'AND',
      conditions: [
        { column: sfocCol, op: '>', value: lo },
        { column: sfocCol, op: '<', value: hi },
        { column: loadCol, op: '>', value: 0 },
      ],
    },
    slice_columns: [time],
  })
    .then((resp) => {
      if (resp.plot_type !== 'scatter' && resp.plot_type !== 'line') {
        put(key, { error: 'Unexpected response' });
        return;
      }
      const ys = resp.series[0]?.values ?? [];
      const ts = resp.slice_values?.[time] ?? null;
      const load: number[] = [];
      const sfoc: number[] = [];
      const times: number[] = [];
      for (let i = 0; i < resp.x.length; i++) {
        const x = resp.x[i];
        const y = ys[i];
        if (x == null || y == null || !Number.isFinite(x) || !Number.isFinite(y)) continue;
        load.push(x);
        sfoc.push(y);
        if (ts) times.push(ts[i] ?? NaN);
      }
      const scaled = load.length > 0 && percentile95(load) <= 1.5;
      if (scaled) for (let i = 0; i < load.length; i++) load[i] = load[i]! * 100;
      put(key, { load, sfoc, time: ts ? times : null, sourceRows: resp.source_rows, scaled });
    })
    .catch((e: unknown) => {
      const message = userMessage(e);
      // Listed in the ship's schema but absent from the files in this range -
      // said in the note's words, not the database's.
      const absentHere = /not found|not in this ship's data|unknown .*column/i.test(message);
      put(key, { error: absentHere ? `no ${sfocCol} data in range` : shortError(message) });
    });
}

/**
 * The points as a density grid, in the shape `/api/plot` returns for a heatmap,
 * so Explore's heatmap painter draws it unchanged. Binned here rather than
 * asked of the server, on the chart's own axes: every selected curve then lands
 * on one grid and their counts add, which separate server grids would not.
 */
export function binHeatmap(
  sets: MeasuredPoints[],
  x: [number, number],
  y: [number, number],
  nx = 40,
  ny = 40,
): HeatmapResponse | null {
  const [x0, x1] = x;
  const [y0, y1] = y;
  if (!(x1 > x0) || !(y1 > y0)) return null;
  const counts = new Array<number>(nx * ny).fill(0);
  const wx = (x1 - x0) / nx;
  const wy = (y1 - y0) / ny;
  let rows = 0;
  for (const set of sets) {
    for (let i = 0; i < set.load.length; i++) {
      const ix = Math.floor((set.load[i]! - x0) / wx);
      const iy = Math.floor((set.sfoc[i]! - y0) / wy);
      if (ix < 0 || iy < 0 || ix >= nx || iy >= ny) continue;
      counts[iy * nx + ix]!++;
      rows++;
    }
  }
  let zMin = Infinity;
  let zMax = 0;
  let occupied = 0;
  for (const n of counts) {
    if (n <= 0) continue;
    occupied++;
    if (n < zMin) zMin = n;
    if (n > zMax) zMax = n;
  }
  if (!occupied) return null;
  const edges = (lo: number, w: number, n: number): number[] =>
    Array.from({ length: n + 1 }, (_, i) => lo + i * w);
  return {
    plot_type: 'heatmap',
    x_column: 'Engine load (%)',
    y_column: 'SFOC (g/kWh)',
    z_column: null,
    z_agg: null,
    nx,
    ny,
    x_step: wx,
    y_step: wy,
    x_edges: edges(x0, wx, nx),
    y_edges: edges(y0, wy, ny),
    counts,
    values: null,
    z_min: zMin,
    z_max: zMax,
    source_rows: rows,
    occupied_cells: occupied,
  };
}

/** A load histogram of one engine's points: `edges` (n + 1) and `counts` (n). */
export function binLoad(
  sets: MeasuredPoints[],
  x: [number, number],
  n = 40,
): { edges: number[]; counts: number[] } {
  const [x0, x1] = x;
  const w = (x1 - x0) / n;
  const counts = new Array<number>(n).fill(0);
  for (const set of sets) {
    for (const v of set.load) {
      const i = Math.floor((v - x0) / w);
      if (i >= 0 && i < n) counts[i]!++;
    }
  }
  return { edges: Array.from({ length: n + 1 }, (_, i) => x0 + i * w), counts };
}
